import { escapeHtml, escapeHtmlAttr } from '../utils/html';
import { getCurrentUserName } from '../api/geotab-client';
import type { GroupHierarchy } from '../api/group-hierarchy';
import { state } from '../state';
import {
  ASSET_TYPE_GROUPS, MIN_LOADER_MS, SPARE_VIN, ARCHIVED_SERIAL, MAX_DEVICE_NAME_LEN,
} from './reset-device-config';
import {
  archiveDevice, addReplacementDevice, appendDeviceComment,
  buildAuditComment, buildFailedReAddNote, buildResolvedNote, reconcileResetState,
  describePermissionFault,
} from './reset-device-data';
import type { RdReconciled } from './reset-device-data';
import type { RdRow, RdFormValues } from './reset-device-types';

// ── Flow state ────────────────────────────────────────────────────────────

let flowRow:       RdRow | null = null;
let flowHierarchy: GroupHierarchy | null = null;
let flowForm:      RdFormValues | null = null;
let submitInFlight = false;
/**
 * Hint that the archive Set succeeded. Deliberately only a hint: the authority
 * on what actually happened is reconcileResetState(), because a caught error
 * never proves the write didn't land.
 */
let archiveDone = false;
/** Set once any write may have reached Geotab, so closing forces a list reload. */
let mayHaveWritten = false;
/** True once a CORRECTION line has been written to the archived record, so a
 *  later successful retry knows to close it out. */
let correctionWritten = false;
let digitTimer: ReturnType<typeof setInterval> | null = null;
let onCompleteCb: (() => void) | null = null;

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// ── Panel switching ───────────────────────────────────────────────────────
// One overlay, five panels. A single overlay avoids z-index stacking and gives
// the flow exactly one backdrop and one close handler to reason about.

type Panel = 'confirm' | 'form' | 'review' | 'loading' | 'outcome';
const PANELS: Panel[] = ['confirm', 'form', 'review', 'loading', 'outcome'];

const PANEL_TITLES: Record<Panel, string> = {
  confirm: 'Reset this device?',
  form:    'New device details',
  review:  'Review before executing',
  loading: 'Resetting device…',
  outcome: 'Result',
};

function setModalTitle(text: string): void {
  const el = document.getElementById('rdModalTitle');
  if (el) el.textContent = text;
}

function showPanel(p: Panel): void {
  PANELS.forEach((name) => {
    document.getElementById(`rdPanel-${name}`)?.classList.toggle('hidden', name !== p);
  });
  setModalTitle(PANEL_TITLES[p]);
  // The loader is not dismissable — a write is in flight and a stray backdrop
  // click must not leave the operator thinking it was cancelled.
  document.getElementById('rdModal')?.classList.toggle('rd-locked', p === 'loading');
  const closeBtn = document.getElementById('rdModalClose') as HTMLButtonElement | null;
  if (closeBtn) closeBtn.style.visibility = p === 'loading' ? 'hidden' : '';
}

export function isRdFlowLocked(): boolean {
  return submitInFlight;
}

export function closeRdFlow(): void {
  if (submitInFlight) return;              // never tear down mid-write
  stopDigits();
  document.getElementById('rdModal')?.classList.remove('show');
  // If anything may have been written, the cached list is now lying — reload.
  const dirty = mayHaveWritten;
  resetFlowState();
  if (dirty) onCompleteCb?.();
}

/** Hard close used by resetResetDevicePage() on logout — ignores the in-flight guard. */
export function forceCloseRdFlow(): void {
  submitInFlight = false;
  stopDigits();
  document.getElementById('rdModal')?.classList.remove('show');
  resetFlowState();
  onCompleteCb = null;
}

function resetFlowState(): void {
  flowRow = null;
  flowForm = null;
  archiveDone = false;
  mayHaveWritten = false;
  correctionWritten = false;
}

// ── Stage 1 — confirmation ────────────────────────────────────────────────

export function openRdFlow(row: RdRow, hierarchy: GroupHierarchy, onComplete: () => void): void {
  flowRow       = row;
  flowHierarchy = hierarchy;
  flowForm      = null;
  archiveDone   = false;
  mayHaveWritten = false;
  onCompleteCb  = onComplete;
  renderConfirmPanel();
}

/**
 * Re-show the confirmation step without touching flow state. Used by Back —
 * routing that through openRdFlow() would clear `archiveDone` and let an
 * already-archived device get archived a second time.
 */
export function backToConfirm(): void {
  if (archiveDone) return;                 // past the point of no return
  renderConfirmPanel();
}

function renderConfirmPanel(): void {
  const row = flowRow;
  if (!row) return;

  const preview = buildAuditComment(row.serialNumber, getCurrentUserName(), new Date());

  const body = document.getElementById('rdConfirmBody');
  if (body) {
    body.innerHTML = `
      <p class="rd-confirm-text">This will archive the current device record (its history is kept,
      but it will no longer be active) and create a brand-new record for this same physical device
      so it can be installed in another vehicle or stored as a spare.
      <strong>This cannot be undone from this screen.</strong></p>

      <div class="rd-target">
        <div class="rd-target-row"><span class="rd-target-k">Device</span><span class="rd-target-v">${escapeHtml(row.name)}</span></div>
        <div class="rd-target-row"><span class="rd-target-k">Serial</span><span class="rd-target-v td-mono">${escapeHtml(row.serialNumber)}</span></div>
        <div class="rd-target-row"><span class="rd-target-k">VIN</span><span class="rd-target-v td-mono">${escapeHtml(row.vin)}</span></div>
      </div>

      <div class="rd-comment-preview">
        <div class="rd-comment-label">Comment that will be logged</div>
        <div class="rd-comment-text td-mono">${escapeHtml(preview)}</div>
      </div>`;
  }

  showPanel('confirm');
  document.getElementById('rdModal')?.classList.add('show');
  (document.getElementById('rdConfirmGo') as HTMLButtonElement | null)?.focus();
}

// ── Stage 2 — re-add form ─────────────────────────────────────────────────

export function showRdForm(): void {
  const row = flowRow;
  if (!row) return;

  const nameInput = document.getElementById('rdFormName') as HTMLInputElement | null;
  const vinInput  = document.getElementById('rdFormVin')  as HTMLInputElement | null;
  const spareCb   = document.getElementById('rdFormSpare') as HTMLInputElement | null;
  const typeSel   = document.getElementById('rdFormAssetType') as HTMLSelectElement | null;

  if (nameInput) nameInput.value = `${row.serialNumber} - Spare`;
  if (spareCb)   spareCb.checked = false;
  if (vinInput) { vinInput.value = ''; vinInput.disabled = false; }
  if (typeSel) {
    typeSel.innerHTML = ASSET_TYPE_GROUPS
      .map((g) => `<option value="${escapeHtmlAttr(g.id)}">${escapeHtml(g.label)}</option>`)
      .join('');
    typeSel.value = 'GroupVehicleId';
  }

  const serialEl = document.getElementById('rdFormSerial');
  if (serialEl) serialEl.textContent = row.serialNumber;

  // Collapse the group picker and drop any leftover search term so a second run
  // starts from the same place the first one did.
  document.getElementById('rdFormGroupPanel')?.classList.add('hidden');
  document.getElementById('rdFormGroupToggle')?.setAttribute('aria-expanded', 'false');
  const groupSearch = document.getElementById('rdFormGroupSearch') as HTMLInputElement | null;
  if (groupSearch) groupSearch.value = '';

  populateRdGroupList();
  clearFormError();
  syncFormBackButton();
  showPanel('form');
  nameInput?.focus();
}

/** Back is only meaningful before the archive lands. Once it has, the only ways
 *  out are Retry re-add or closing (which reloads the list). */
function syncFormBackButton(): void {
  document.getElementById('rdFormBack')?.classList.toggle('hidden', archiveDone);
}

/** Customer groups only — buildGroupHierarchy already strips Geotab's built-ins,
 *  including the four asset-type groups the dropdown above owns. */
function populateRdGroupList(): void {
  const list = document.getElementById('rdFormGroupList');
  if (!list) return;

  const nodes = flowHierarchy?.groups ?? [];
  if (!nodes.length) {
    list.innerHTML = '<div class="rd-group-none">No customer groups in this database.</div>';
    return;
  }

  list.innerHTML = nodes.map(({ id, name, depth }) => {
    const pad = depth > 0 ? ` style="padding-left:${(0.75 + depth * 0.85).toFixed(2)}rem"` : '';
    return `<label class="vselector-item"${pad}>`
         + `<input type="checkbox" value="${escapeHtmlAttr(id)}">`
         + `<span class="vselector-item-name">${escapeHtml(name)}</span></label>`;
  }).join('');

  updateRdGroupLabel();
}

export function getRdSelectedGroupIds(): string[] {
  const list = document.getElementById('rdFormGroupList');
  if (!list) return [];
  return [...list.querySelectorAll<HTMLInputElement>('input[type=checkbox]:checked')].map((cb) => cb.value);
}

export function updateRdGroupLabel(): void {
  const label = document.getElementById('rdFormGroupLabel');
  if (!label) return;
  const n = getRdSelectedGroupIds().length;
  const none = (flowHierarchy?.groups.length ?? 0) === 0;
  label.textContent = n > 0
    ? `${n} group${n === 1 ? '' : 's'} selected`
    : none ? 'No groups in this database' : 'Select at least one group';
  label.classList.toggle('rd-group-unset', n === 0 && !none);
}

/** "Mark as Spare" overrides the VIN field: disable it and show the literal value. */
export function applySpareToggle(): void {
  const vinInput = document.getElementById('rdFormVin')  as HTMLInputElement | null;
  const spareCb  = document.getElementById('rdFormSpare') as HTMLInputElement | null;
  if (!vinInput || !spareCb) return;

  if (spareCb.checked) {
    vinInput.dataset.prev = vinInput.value;
    vinInput.value    = SPARE_VIN;
    vinInput.disabled = true;
  } else {
    vinInput.disabled = false;
    vinInput.value    = vinInput.dataset.prev ?? '';
  }
}

/**
 * Validate everything checkable before any write happens. A rejection that
 * surfaces *after* the archive is the worst possible failure mode, so anything
 * that can be caught here must be.
 */
function readForm(): RdFormValues | null {
  const row = flowRow;
  if (!row) return null;

  const name   = (document.getElementById('rdFormName') as HTMLInputElement | null)?.value.trim() ?? '';
  const spare  = (document.getElementById('rdFormSpare') as HTMLInputElement | null)?.checked ?? false;
  const vinRaw = (document.getElementById('rdFormVin') as HTMLInputElement | null)?.value.trim().toUpperCase() ?? '';
  const type   = (document.getElementById('rdFormAssetType') as HTMLSelectElement | null)?.value ?? '';

  if (!name) { showFormError('Enter a name for the new device record.'); return null; }
  if (name.length > MAX_DEVICE_NAME_LEN) {
    showFormError(`Name is ${name.length} characters; Geotab allows at most ${MAX_DEVICE_NAME_LEN}.`);
    return null;
  }
  if (!type) { showFormError('Choose an asset type.'); return null; }
  if (!spare && !vinRaw) {
    showFormError('Enter a VIN, or switch on “Mark as Spare”.');
    return null;
  }
  if (!spare && vinRaw.length > 17) {
    showFormError(`VIN is ${vinRaw.length} characters; a VIN is at most 17.`);
    return null;
  }

  // At least one customer group is mandatory. A device that lands only in the
  // asset-type group (Vehicle/Trailer/…) sits at the root of the hierarchy,
  // where nobody scoped to a child group can see it — from their side the
  // vehicle has simply vanished. The one exception is a database with no
  // customer groups at all, where the requirement would be unsatisfiable.
  const groupIds = getRdSelectedGroupIds();
  const groupsAvailable = (flowHierarchy?.groups.length ?? 0) > 0;
  if (groupsAvailable && groupIds.length === 0) {
    showFormError('Select at least one group. Without one the new device sits at the top of the '
                + 'hierarchy and users scoped to a sub-group will not be able to see it.');
    openGroupPicker();
    return null;
  }

  return {
    name,
    serialNumber:     row.serialNumber,
    vin:              spare ? SPARE_VIN : vinRaw,
    assetTypeGroupId: type,
    groupIds,
  };
}

/** Expand the group picker so a validation failure points at the control. */
function openGroupPicker(): void {
  document.getElementById('rdFormGroupPanel')?.classList.remove('hidden');
  document.getElementById('rdFormGroupToggle')?.setAttribute('aria-expanded', 'true');
  document.getElementById('rdFormGroupToggle')?.scrollIntoView({ block: 'nearest' });
}

function showFormError(msg: string): void {
  const el = document.getElementById('rdFormError');
  if (el) { el.textContent = msg; el.classList.remove('hidden'); }
}

function clearFormError(): void {
  document.getElementById('rdFormError')?.classList.add('hidden');
}

// ── Stage 3 — review ──────────────────────────────────────────────────────

/** Last chance to read back exactly what is about to change, before any write. */
export function showRdReview(): void {
  const row = flowRow;
  if (!row) return;

  const form = readForm();
  if (!form) return;                       // validation errors stay on the form
  flowForm = form;

  const typeLabel = ASSET_TYPE_GROUPS.find((g) => g.id === form.assetTypeGroupId)?.label ?? form.assetTypeGroupId;
  const groupNames = form.groupIds
    .map((id) => flowHierarchy?.groupMap.get(id) ?? id)
    .filter(Boolean);

  const body = document.getElementById('rdReviewBody');
  if (body) {
    body.innerHTML = `
      <div class="rd-review-block rd-review-archive">
        <div class="rd-review-head"><span class="rd-review-tag">Archived</span> existing record becomes inactive</div>
        <div class="rd-review-rows">
          <div><span class="rd-target-k">Device</span><span class="rd-target-v">${escapeHtml(row.name)}</span></div>
          <div><span class="rd-target-k">Serial</span><span class="rd-target-v td-mono">${escapeHtml(row.serialNumber)} &rarr; ${escapeHtml(ARCHIVED_SERIAL)}</span></div>
        </div>
      </div>

      <div class="rd-review-arrow" aria-hidden="true">&darr;</div>

      <div class="rd-review-block rd-review-keep">
        <div class="rd-review-head"><span class="rd-review-tag">Retained</span> physical serial is reused</div>
        <div class="rd-review-rows">
          <div><span class="rd-target-k">Serial</span><span class="rd-target-v td-mono">${escapeHtml(row.serialNumber)}</span></div>
        </div>
      </div>

      <div class="rd-review-arrow" aria-hidden="true">&darr;</div>

      <div class="rd-review-block rd-review-create">
        <div class="rd-review-head"><span class="rd-review-tag">Created</span> new active record</div>
        <div class="rd-review-rows">
          <div><span class="rd-target-k">Name</span><span class="rd-target-v">${escapeHtml(form.name)}</span></div>
          <div><span class="rd-target-k">VIN</span><span class="rd-target-v td-mono">${escapeHtml(form.vin)}</span></div>
          <div><span class="rd-target-k">Type</span><span class="rd-target-v">${escapeHtml(typeLabel)}</span></div>
          <div><span class="rd-target-k">Groups</span><span class="rd-target-v">${
            groupNames.length
              ? escapeHtml(groupNames.join(', '))
              : '<em>none — this database has no customer groups</em>'
          }</span></div>
        </div>
      </div>`;
  }

  showPanel('review');
  (document.getElementById('rdReviewGo') as HTMLButtonElement | null)?.focus();
}

// ── Stage 4 — the write, behind the matrix loader ─────────────────────────

type StepKey = 'validate' | 'archive' | 'create' | 'verify';
const STEP_ORDER: StepKey[] = ['validate', 'archive', 'create', 'verify'];

function setStep(key: StepKey, statusClass: 'active' | 'done' | 'failed'): void {
  const el = document.getElementById(`rdStep-${key}`);
  if (!el) return;
  el.classList.remove('rd-step-active', 'rd-step-done', 'rd-step-failed');
  el.classList.add(`rd-step-${statusClass}`);
}

function resetSteps(): void {
  STEP_ORDER.forEach((k) => {
    document.getElementById(`rdStep-${k}`)
      ?.classList.remove('rd-step-active', 'rd-step-done', 'rd-step-failed');
  });
}

export async function submitRdFlow(): Promise<void> {
  if (submitInFlight) return;                       // double-click must not issue two Adds
  const row = flowRow;
  if (!row) return;

  const form = flowForm ?? readForm();
  if (!form) return;
  flowForm = form;

  submitInFlight = true;
  const startedAt = Date.now();
  const epoch     = state.sessionEpoch;

  resetSteps();
  startDigits();
  showPanel('loading');

  try {
    setStep('validate', 'active');

    if (!archiveDone) {
      setStep('validate', 'done');
      setStep('archive', 'active');
      // From here on a write may have reached Geotab even if we later throw.
      mayHaveWritten = true;
      const auditLine = buildAuditComment(row.serialNumber, getCurrentUserName(), new Date());
      await archiveDevice(row.id, auditLine, row.serialNumber);
      archiveDone = true;
      setStep('archive', 'done');
    } else {
      setStep('validate', 'done');
      setStep('archive', 'done');
    }

    setStep('create', 'active');
    let newId = await addReplacementDevice(form);
    setStep('create', 'done');

    // Never claim success on an unverified outcome: if Add returned no usable
    // id, go and look rather than assuming it worked.
    setStep('verify', 'active');
    if (!newId) {
      const check = await reconcileResetState(row.id, row.serialNumber);
      if (check.kind === 'completed') {
        newId = check.newId;
      } else {
        setStep('verify', 'failed');
        if (epoch !== state.sessionEpoch) { finishWrite(); return; }
        finishWrite();
        showUnverified(form, check);
        return;
      }
    }

    // In the normal path the archive comment already reads exactly as required
    // and nothing more is appended. Only when a previous attempt left a
    // CORRECTION line does the record need closing out. Non-fatal either way:
    // the reset itself has already succeeded.
    let commentWarning = '';
    if (correctionWritten) {
      try {
        await appendDeviceComment(row.id, buildResolvedNote(newId, form.name));
        correctionWritten = false;
      } catch (err) {
        commentWarning = (err as Error).message;
      }
    }
    setStep('verify', 'done');

    // Logged out (or switched database) while the write was in flight — the
    // writes landed, but this session must not paint over the new one's UI.
    if (epoch !== state.sessionEpoch) { finishWrite(); return; }

    // Hold the loader to its floor only on success; failures surface at once.
    const remaining = MIN_LOADER_MS - (Date.now() - startedAt);
    if (remaining > 0) await sleep(remaining);
    if (epoch !== state.sessionEpoch) { finishWrite(); return; }

    finishWrite();
    showSuccess(form.name, newId, commentWarning);
    onCompleteCb?.();
  } catch (err) {
    const message = (err as Error).message;

    // A rejected request does not prove the write didn't land. Read back the
    // real state before telling the operator anything.
    const actual = await reconcileResetState(row.id, row.serialNumber);

    // The archive comment claims the device was re-added. If the re-add did not
    // in fact happen, amend the record rather than leaving a permanent
    // overstatement. Best-effort: the same outage or clearance gap that broke
    // the Add may well break this too, and it must not mask the real error.
    if (actual.kind === 'archived' && !correctionWritten) {
      try {
        await appendDeviceComment(row.id, buildFailedReAddNote());
        correctionWritten = true;
      } catch { /* the outcome screen already tells the operator what to do */ }
    }

    finishWrite();
    if (epoch !== state.sessionEpoch) return;
    showFailure(actual, message, form);
  }
}

function finishWrite(): void {
  submitInFlight = false;
  stopDigits();
}

// ── Stage 5 — outcome ─────────────────────────────────────────────────────

function idBlock(row: RdRow, newId: string): string {
  return `<div class="rd-outcome-ids">
    <div><span class="rd-target-k">Serial</span> <span class="td-mono">${escapeHtml(row.serialNumber)}</span></div>
    <div><span class="rd-target-k">Old device ID</span> <span class="td-mono">${escapeHtml(row.id)}</span></div>
    ${newId ? `<div><span class="rd-target-k">New device ID</span> <span class="td-mono">${escapeHtml(newId)}</span></div>` : ''}
  </div>`;
}

function showSuccess(newName: string, newId: string, commentWarning: string): void {
  const row = flowRow;
  archiveDone = false;
  const body = document.getElementById('rdOutcomeBody');
  if (body) {
    body.innerHTML = `
      <div class="rd-outcome rd-outcome-ok">
        <div class="rd-outcome-icon">✓</div>
        <div class="rd-outcome-title">Device reset complete. New device “${escapeHtml(newName)}” is now active.</div>
        ${row ? idBlock(row, newId) : ''}
        <p class="rd-outcome-note">The old record is retained with its full history and now appears
        only under an archived/historical filter.</p>
        ${commentWarning ? `<p class="rd-outcome-note rd-outcome-warn">The reset succeeded, but the
          closing audit note could not be written to the archived record: ${escapeHtml(commentWarning)}</p>` : ''}
      </div>`;
  }
  setOutcomeButtons({ retry: false, doneLabel: 'Done' });
  showPanel('outcome');
  setModalTitle('Device reset complete');
  (document.getElementById('rdOutcomeDone') as HTMLButtonElement | null)?.focus();
}

/** Add returned no id and reconciliation couldn't confirm a replacement. */
function showUnverified(form: RdFormValues, check: RdReconciled): void {
  const row = flowRow;
  const detail = check.kind === 'conflict' || check.kind === 'unknown' ? check.detail : '';
  const body = document.getElementById('rdOutcomeBody');
  if (body) {
    body.innerHTML = `
      <div class="rd-outcome rd-outcome-warnbox">
        <div class="rd-outcome-icon">?</div>
        <div class="rd-outcome-title">Completed, but the replacement could not be verified.</div>
        <p class="rd-outcome-note">Geotab accepted the request without returning an ID, and a follow-up
        check did not find an active device on serial
        <strong>${escapeHtml(form.serialNumber)}</strong>. Confirm in MyGeotab before installing the
        hardware; do not run the reset again until you have.</p>
        ${detail ? `<div class="rd-outcome-reason td-mono">${escapeHtml(detail)}</div>` : ''}
        ${row ? idBlock(row, '') : ''}
      </div>`;
  }
  setOutcomeButtons({ retry: false, doneLabel: 'Close' });
  showPanel('outcome');
  setModalTitle('Needs verification');
}

/**
 * Report what reconciliation actually found, not what the thrown error implied.
 * `completed` is reachable here: Geotab can commit both writes and still have
 * the response fail, in which case the operation genuinely succeeded.
 */
function showFailure(actual: RdReconciled, message: string, form: RdFormValues): void {
  const row = flowRow;

  if (actual.kind === 'completed') {
    archiveDone = false;
    const body = document.getElementById('rdOutcomeBody');
    if (body) {
      body.innerHTML = `
        <div class="rd-outcome rd-outcome-ok">
          <div class="rd-outcome-icon">✓</div>
          <div class="rd-outcome-title">Device reset completed. New device “${escapeHtml(actual.newName)}” is active.</div>
          <p class="rd-outcome-note">The connection reported an error, but a check of your database
          shows both steps landed. No further action is needed.</p>
          <div class="rd-outcome-reason td-mono">Reported error: ${escapeHtml(message)}</div>
          ${row ? idBlock(row, actual.newId) : ''}
        </div>`;
    }
    setOutcomeButtons({ retry: false, doneLabel: 'Done' });
    showPanel('outcome');
    setModalTitle('Device reset complete');
    onCompleteCb?.();
    return;
  }

  const stranded = actual.kind === 'archived';
  // Only offer a retry when we know the archive landed and nothing replaced it.
  archiveDone = stranded;

  let title: string;
  let note: string;
  if (stranded) {
    title = 'The device was archived, but the replacement record was not created.';
    note = `<p class="rd-outcome-note rd-outcome-warn">
      <strong>${escapeHtml(form.serialNumber)}</strong> is archived and its serial is free, but no
      active record exists for it yet. Retry below to create the replacement — the archive step will
      not run again. If you leave now, finish the re-add in MyGeotab.</p>`;
  } else if (actual.kind === 'untouched') {
    title = 'Nothing was changed — the device could not be archived.';
    note = '<p class="rd-outcome-note">A check of your database confirms the device is still active and unmodified. You can safely close this and try again.</p>';
  } else if (actual.kind === 'conflict') {
    title = 'The database is in an unexpected state — stopped without retrying.';
    note = `<p class="rd-outcome-note rd-outcome-warn">${escapeHtml(actual.detail)}
      Resolve this in MyGeotab before running another reset on this serial.</p>`;
  } else {
    title = 'The operation failed, and its outcome could not be confirmed.';
    note = `<p class="rd-outcome-note rd-outcome-warn">The follow-up check also failed
      (${escapeHtml(actual.detail)}), so the device may or may not have been archived.
      <strong>Check this device in MyGeotab before retrying</strong> — retrying blind could archive it twice.</p>`;
  }

  // A clearance denial is a different kind of problem from a failed write: it is
  // the operator's account, not the data, and it has a concrete remedy. Give it
  // its own framing instead of dumping Geotab's 50-line access list.
  const perm = describePermissionFault(message);
  if (perm.isPermission) {
    title = 'You don’t have permission to reset devices.';
    note = `<p class="rd-outcome-note">Your Geotab account${
        getCurrentUserName() ? ` (<strong>${escapeHtml(getCurrentUserName())}</strong>)` : ''
      } is missing the clearance needed to archive and re-add devices.
      ${actual.kind === 'untouched'
        ? 'Nothing was changed.'
        : 'See the state note below before retrying.'}
      Ask your Geotab administrator to grant <strong>any one</strong> of these, then try again:</p>
      ${perm.clearances.length
        ? `<div class="rd-clearances">${perm.clearances
            .map((c) => `<span class="rd-clearance">${escapeHtml(c)}</span>`).join('')}</div>`
        : ''}
      <p class="rd-outcome-note rd-outcome-hint">In MyGeotab these live under
      <em>Administration &rsaquo; Users &rsaquo; Security Clearances</em>. Confirmed working:
      <em>Administer assets (delete/unplug/replace)</em>, i.e.
      <span class="td-mono">DeviceAdminDeleteUnplugReplace</span> — it covers both the archive
      and the re-add.</p>`;
  }

  const body = document.getElementById('rdOutcomeBody');
  if (body) {
    // The raw fault is kept, just collapsed — nothing is hidden from the operator.
    const reason = perm.isPermission
      ? `<details class="rd-raw-fault"><summary>Show full Geotab response</summary>
           <div class="rd-outcome-reason td-mono">${escapeHtml(message)}</div></details>`
      : `<div class="rd-outcome-reason td-mono">${escapeHtml(message)}</div>`;

    body.innerHTML = `
      <div class="rd-outcome rd-outcome-err">
        <div class="rd-outcome-icon">${perm.isPermission ? '🔒' : '!'}</div>
        <div class="rd-outcome-title">${escapeHtml(title)}</div>
        ${note}
        ${reason}
        ${row ? idBlock(row, '') : ''}
      </div>`;
  }
  setOutcomeButtons({ retry: stranded, doneLabel: 'Close' });
  showPanel('outcome');
  setModalTitle(perm.isPermission
    ? 'Permission required'
    : stranded ? 'Re-add failed — action needed' : 'Reset failed');
}

/**
 * Dismiss is neutral; the action that re-issues a write is the prominent one.
 * The previous wiring had these inverted — a benign "Done" styled destructive,
 * with the meaningful "Retry re-add" receding into a ghost button.
 */
function setOutcomeButtons(opts: { retry: boolean; doneLabel: string }): void {
  const retry = document.getElementById('rdOutcomeRetry');
  retry?.classList.toggle('hidden', !opts.retry);
  const done = document.getElementById('rdOutcomeDone');
  if (done) done.textContent = opts.doneLabel;
}

/** Retry from the outcome panel: back to the form with values intact. Because
 *  `archiveDone` stays true, submitting re-runs only the Add. */
export function retryRdAdd(): void {
  clearFormError();
  syncFormBackButton();
  showPanel('form');
  (document.getElementById('rdFormName') as HTMLInputElement | null)?.focus();
}

// ── Matrix loader digits ──────────────────────────────────────────────────

const DIGIT_COUNT = 8;

function paintDigits(): void {
  const host = document.getElementById('rdMatrixLoader');
  if (!host) return;
  host.querySelectorAll<HTMLElement>('.digit').forEach((d) => {
    d.textContent = Math.random() < 0.5 ? '0' : '1';
  });
}

function startDigits(): void {
  const host = document.getElementById('rdMatrixLoader');
  if (host && !host.querySelector('.digit')) {
    host.innerHTML = Array.from({ length: DIGIT_COUNT }, () => '<div class="digit"></div>').join('')
                   + '<div class="glow"></div>';
  }
  paintDigits();
  if (!digitTimer) digitTimer = setInterval(paintDigits, 220);
}

function stopDigits(): void {
  if (digitTimer) { clearInterval(digitTimer); digitTimer = null; }
}
